"use client"

import { createPortal } from "react-dom"
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import ChartPanel from "../../charts/ChartPanel"
import { formatCompactNumber, formatNumber, formatPercent } from "../../charts/formatters"
import {
  buildMessageLegendTierRows,
  computeBandSummaries,
  computeVolumeBandEdges,
  getVolumeBand,
  messageVolumeTierLabel,
  messageVolumeTierTooltipPhrase,
  VOLUME_BAND_COUNT,
} from "../utils/messageVolumeTierModel"
import {
  bestPeakHourWindow,
  heatmapLegendSwatchFill,
  heatmapSegmentFill,
  hourTotalsFromMatrix24,
  peakBandStyleFromHours,
  shouldShowPeakBand,
  topThreePeakSlotKeys,
  xAxisLabelStep,
} from "../utils/messageHeatmapReadability"
import { expandTrimmedHourlyMatrixTo24, parseHourLabel12ToIndex } from "../utils/optimalPostingTimesAdapter"
import "../../charts/chart.css"
import "./OptimalPostingTimesWidget.css"
import "./MessageHeatmapWidget.css"
import "./MessageVolumeDailyWidget.css"

export const MESSAGE_HEATMAP_CHART_ID = "messageHeatmap"

const MONTHLY_BAR_LAYOUT = { trackPx: 58, gapPx: 16, colWidthPx: 34 }

/** Same 24h Y-axis tick set as Optimal Posting Times unified chart */
const HOURS_24 = Array.from({ length: 24 }, (_, i) => i)
const Y_AXIS_HOURS = [22, 20, 18, 16, 14, 12, 10, 8, 6, 4, 2, 0]

/** Column order + ISO weekday index (Mon=0 … Sun=6); maps to Sun-first mock columns */
const DISPLAY_DAYS = [
  { label: "Sun", dataIndex: 6, weekend: true },
  { label: "Mon", dataIndex: 0, weekend: false },
  { label: "Tue", dataIndex: 1, weekend: false },
  { label: "Wed", dataIndex: 2, weekend: false },
  { label: "Thu", dataIndex: 3, weekend: false },
  { label: "Fri", dataIndex: 4, weekend: false },
  { label: "Sat", dataIndex: 5, weekend: true },
]

function formatAxisHour(h) {
  const x = Math.max(0, Math.min(23, Math.floor(h)))
  const isPM = x >= 12
  const d = x === 0 ? 12 : x > 12 ? x - 12 : x
  return `${d}:00${isPM ? "pm" : "am"}`
}

function formatPeakHourCompact(h) {
  const x = Math.max(0, Math.min(23, Math.floor(h)))
  const isPM = x >= 12
  const d = x === 0 ? 12 : x > 12 ? x - 12 : x
  return `${d}${isPM ? "PM" : "AM"}`
}

/** Mock weekly matrix is Sun=0 … Sat=6; DISPLAY_DAYS uses ISO day index */
function isoDayToSunFirstCol(dataIndex) {
  return dataIndex === 6 ? 0 : dataIndex + 1
}

function shortenDay(day) {
  if (!day || typeof day !== "string") return day
  if (day.length <= 3) return day
  return day.slice(0, 3)
}

function parseISODate(iso) {
  if (!iso || typeof iso !== "string") return null
  const [y, m, d] = iso.split("-").map(Number)
  if (!y || !m || !d) return null
  return new Date(y, m - 1, d)
}

function formatTooltipDate(iso) {
  const dt = parseISODate(iso)
  if (!dt) return iso
  return dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })
}

function formatAxisDate(iso) {
  const dt = parseISODate(iso)
  if (!dt) return iso
  const mo = dt.toLocaleDateString("en-US", { month: "short" })
  const day = dt.getDate()
  return `${mo} ${day}`
}

function isWeekendIso(iso) {
  const dt = parseISODate(iso)
  if (!dt) return false
  const dow = dt.getDay()
  return dow === 0 || dow === 6
}

function formatMonthAxis(ym) {
  if (!ym || typeof ym !== "string") return ym
  const [y, m] = ym.split("-").map(Number)
  if (!y || !m) return ym
  const dt = new Date(y, m - 1, 1)
  return dt.toLocaleDateString("en-US", { month: "short", year: "2-digit" })
}

function formatMonthTooltip(ym) {
  if (!ym || typeof ym !== "string") return ym
  const [y, m] = ym.split("-").map(Number)
  if (!y || !m) return ym
  const dt = new Date(y, m - 1, 1)
  return dt.toLocaleDateString("en-US", { month: "long", year: "numeric" })
}

function measureHeatmapTooltipLayout(anchorEl) {
  const r = anchorEl.getBoundingClientRect()
  const vw = window.innerWidth
  const vh = window.innerHeight
  const halfW = 110
  const approxH = 96
  const gutter = 10

  let x = r.left + r.width / 2
  x = Math.max(halfW + gutter, Math.min(vw - halfW - gutter, x))

  const spaceAbove = r.top
  const spaceBelow = vh - r.bottom
  const placeAbove = spaceAbove >= approxH + gutter || spaceAbove >= spaceBelow

  if (placeAbove) {
    return { x, y: r.top - 8, transform: "translate(-50%, -100%)" }
  }
  return { x, y: r.bottom + 8, transform: "translate(-50%, 0)" }
}

/**
 * Message volume heatmap: Daily / Weekly / Monthly (layout and chrome aligned with Optimal Posting Times unified chart).
 */
export default function MessageHeatmapUnifiedWidget({
  weeklyHours,
  weeklyDays,
  weeklyValues,
  calendarDates,
  hourlyDailyMatrix,
  monthlyHourlyBundle,
  dateRangeLabel,
  propertyTimezone,
  footerActions,
  loading = false,
}) {
  const viewMode = "weekly"
  const [hoveredCell, setHoveredCell] = useState(null)
  const [pinnedBand, setPinnedBand] = useState(null)
  const plotRef = useRef(null)
  const tooltipAnchorRef = useRef(null)
  const [tooltipLayout, setTooltipLayout] = useState(null)

  const wHours = Array.isArray(weeklyHours) ? weeklyHours : []
  const wDays = Array.isArray(weeklyDays) ? weeklyDays : []
  const wMatrix = useMemo(() => {
    if (!Array.isArray(weeklyValues) || wHours.length === 0 || wDays.length === 0) return []
    return weeklyValues
      .slice(0, wHours.length)
      .map((row) => (Array.isArray(row) ? row.slice(0, wDays.length).map((x) => Number(x) || 0) : Array(wDays.length).fill(0)))
  }, [weeklyValues, wHours.length, wDays.length])

  const calDates = Array.isArray(calendarDates) ? calendarDates : []
  const calN = calDates.length
  const hourList = Array.isArray(hourlyDailyMatrix?.hours) ? hourlyDailyMatrix.hours : []
  const hourCount = hourList.length
  const dailyMatrix = useMemo(() => {
    if (!Array.isArray(hourlyDailyMatrix?.matrix) || hourCount === 0 || calN === 0) return []
    return hourlyDailyMatrix.matrix
      .slice(0, hourCount)
      .map((row) => (Array.isArray(row) ? row.slice(0, calN).map((x) => Number(x) || 0) : Array(calN).fill(0)))
  }, [hourlyDailyMatrix, hourCount, calN])

  const monthDates = Array.isArray(monthlyHourlyBundle?.dates) ? monthlyHourlyBundle.dates : []
  const monthN = monthDates.length
  const monthHourList = Array.isArray(monthlyHourlyBundle?.hours) ? monthlyHourlyBundle.hours : []
  const monthHourCount = monthHourList.length
  const monthMatrix = useMemo(() => {
    if (!Array.isArray(monthlyHourlyBundle?.matrix) || monthHourCount === 0 || monthN === 0) return []
    return monthlyHourlyBundle.matrix
      .slice(0, monthHourCount)
      .map((row) => (Array.isArray(row) ? row.slice(0, monthN).map((x) => Number(x) || 0) : Array(monthN).fill(0)))
  }, [monthlyHourlyBundle, monthHourCount, monthN])

  /** Full 24×7 from coarse weekly buckets (same hour scale as daily/monthly / optimal posting) */
  const weeklyMatrix24 = useMemo(() => {
    const nCol = wDays.length
    if (wHours.length === 0 || nCol === 0) return []
    const out = Array.from({ length: 24 }, () => Array(nCol).fill(0))
    for (let r = 0; r < wHours.length; r += 1) {
      const h = parseHourLabel12ToIndex(wHours[r])
      if (h == null || h < 0 || h > 23) continue
      for (let c = 0; c < nCol; c += 1) {
        out[h][c] = Number(wMatrix[r]?.[c] ?? 0)
      }
    }
    return out
  }, [wHours, wDays.length, wMatrix])

  const { matrix: dailyMatrix24 } = useMemo(
    () => expandTrimmedHourlyMatrixTo24(dailyMatrix, dailyMatrix, hourList, calN),
    [dailyMatrix, hourList, calN],
  )

  const { matrix: monthMatrix24 } = useMemo(
    () => expandTrimmedHourlyMatrixTo24(monthMatrix, monthMatrix, monthHourList, monthN),
    [monthMatrix, monthHourList, monthN],
  )

  const activeMatrix = useMemo(() => weeklyMatrix24, [weeklyMatrix24])

  const total = useMemo(() => activeMatrix.flat().reduce((sum, v) => sum + v, 0), [activeMatrix])

  const volumeBands = useMemo(() => computeVolumeBandEdges(activeMatrix), [activeMatrix])

  const bandSummaries = useMemo(
    () => computeBandSummaries(activeMatrix, volumeBands.edges, volumeBands.bandCount, total),
    [activeMatrix, volumeBands.edges, volumeBands.bandCount, total],
  )

  const wTotal = useMemo(() => weeklyMatrix24.flat().reduce((s, v) => s + v, 0), [weeklyMatrix24])
  const dailyTotal = useMemo(() => dailyMatrix24.flat().reduce((s, v) => s + v, 0), [dailyMatrix24])
  const monthGrandTotal = useMemo(() => monthMatrix24.flat().reduce((s, v) => s + v, 0), [monthMatrix24])

  const hasEnoughWeekly = wDays.length > 0 && wHours.length > 0 && wTotal > 0
  const hasEnoughDaily = calN > 0 && hourCount > 0 && dailyTotal > 0
  const hasEnoughMonthly = monthN > 0 && monthHourCount > 0 && monthGrandTotal > 0

  const hasEnoughActive = hasEnoughWeekly

  const hourTotalsReadability = useMemo(() => hourTotalsFromMatrix24(activeMatrix), [activeMatrix])

  const peakWindowReadability = useMemo(() => bestPeakHourWindow(hourTotalsReadability, 4), [hourTotalsReadability])

  const showPeakHourBand = useMemo(
    () =>
      hasEnoughActive &&
      activeMatrix.length === 24 &&
      peakWindowReadability != null &&
      shouldShowPeakBand(hourTotalsReadability, peakWindowReadability, total),
    [hasEnoughActive, activeMatrix.length, peakWindowReadability, hourTotalsReadability, total],
  )

  const peakBandLayoutStyle = useMemo(() => {
    if (!showPeakHourBand || !peakWindowReadability) return null
    return peakBandStyleFromHours(peakWindowReadability.startH, peakWindowReadability.endH)
  }, [showPeakHourBand, peakWindowReadability])

  const topPeakKeySet = useMemo(() => topThreePeakSlotKeys(activeMatrix), [activeMatrix])

  const weeklyPeakSubtitle = useMemo(() => {
    if (!Array.isArray(weeklyMatrix24) || weeklyMatrix24.length === 0) {
      return "Peak message times by day of week"
    }
    let bestValue = 0
    let bestHour = null
    let bestCol = null
    for (let h = 0; h < 24; h += 1) {
      const row = weeklyMatrix24[h]
      if (!Array.isArray(row)) continue
      for (let c = 0; c < row.length; c += 1) {
        const v = Number(row[c] || 0)
        if (v > bestValue) {
          bestValue = v
          bestHour = h
          bestCol = c
        }
      }
    }
    if (bestHour == null || bestCol == null || bestValue <= 0) {
      return "Peak message times by day of week"
    }
    const dayLabel = shortenDay(wDays[bestCol] || DISPLAY_DAYS[bestCol]?.label || "")
    const hourLabel = formatPeakHourCompact(bestHour)
    if (!dayLabel || !hourLabel) return "Peak message times by day of week"
    return `Peak message times by day of week · Peak: ${dayLabel} ${hourLabel}`
  }, [weeklyMatrix24, wDays])

  const dailyXLabelStep = useMemo(() => xAxisLabelStep(calN, "daily"), [calN])
  const monthlyXLabelStep = useMemo(() => xAxisLabelStep(monthN, "monthly"), [monthN])

  const titleStatsLine = useMemo(() => {
    const totalLabel = `${formatCompactNumber(total)} messages`
    if (calN > 0) return `${totalLabel} (${calN}d)`
    return totalLabel
  }, [total, calN])

  const panelTitle = useMemo(
    () => (
      <>
        <span className="messageHeatmapPanelTitleMain">Message Heatmap</span>
        <span className="messageHeatmapPanelTitleStats dashboard-tabular-nums">{titleStatsLine}</span>
      </>
    ),
    [titleStatsLine],
  )

  const actions = null
  const useScrollX = false

  const chartLayoutVars = useMemo(() => {
    const base = { "--messageVolumeDailyPlotBaseHeight": "260px" }
    return {
      ...base,
      "--messageVolumeDailyColTrack": "42px",
      "--messageVolumeDailyColWidth": "15px",
      "--messageVolumeDailyColGap": "3px",
    }
  }, [])

  const monthlyClusterStyle = useMemo(() => {
    if (monthN < 1) return {}
    const { trackPx, gapPx } = MONTHLY_BAR_LAYOUT
    const w = monthN * trackPx + (monthN - 1) * gapPx
    return { "--optimalPostingMonthlyClusterPx": `${w}px` }
  }, [monthN])

  const chartViewShiftAttr = "none"

  const togglePinBand = useCallback((level) => {
    setPinnedBand((prev) => (prev === level ? null : level))
  }, [])

  const isHoveredOnly = (hourIndex, col) => hoveredCell?.hourIndex === hourIndex && hoveredCell?.col === col

  useEffect(() => {
    setHoveredCell(null)
    setPinnedBand(null)
  }, [])

  useEffect(() => {
    if (pinnedBand != null && plotRef.current) {
      plotRef.current.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" })
    }
  }, [pinnedBand])

  useLayoutEffect(() => {
    if (!hoveredCell || !tooltipAnchorRef.current) {
      setTooltipLayout(null)
      return undefined
    }
    const el = tooltipAnchorRef.current
    const measure = () => {
      setTooltipLayout(measureHeatmapTooltipLayout(el))
    }
    measure()
    window.addEventListener("scroll", measure, true)
    window.addEventListener("resize", measure)
    return () => {
      window.removeEventListener("scroll", measure, true)
      window.removeEventListener("resize", measure)
    }
  }, [hoveredCell])

  const hoveredHeatLevel =
    hoveredCell != null
      ? getVolumeBand(hoveredCell.value, volumeBands.edges, volumeBands.bandCount)
      : 0

  const tooltipTitle =
    hoveredCell?.mode === "weekly"
      ? `${shortenDay(hoveredCell.day)} · ${hoveredCell.hourLabel}`
      : ""

  const barTooltipEl =
    hoveredCell &&
    tooltipLayout &&
    createPortal(
      <div
        className="messageHeatmapBarTooltipAnchor"
        role="tooltip"
        style={{
          position: "fixed",
          left: tooltipLayout.x,
          top: tooltipLayout.y,
          transform: tooltipLayout.transform,
          zIndex: 6000,
        }}
      >
        <div className="rg-chart__tooltip rg-chart__tooltip--campaign messageHeatmapBarTooltipPanel optimalPostingTimesBarTooltipPanel">
          <div className="rg-chart__tooltip-title">{tooltipTitle}</div>
          <div className="rg-chart__tooltip-row messageHeatmapBarTooltipRow optimalPostingTimesBarTooltipRow">
            <span className="rg-chart__tooltip-rowLabel">Messages</span>
            <span className="rg-chart__tooltip-value dashboard-tabular-nums">{formatNumber(hoveredCell.value)}</span>
          </div>
          <div className="rg-chart__tooltip-row messageHeatmapBarTooltipRow optimalPostingTimesBarTooltipRow">
            <span className="rg-chart__tooltip-rowLabel">
              <span className="optimalPostingTimesBarTooltipLabelStack">
                <span className="optimalPostingTimesBarTooltipLabelMain">Volume share</span>
                <span className="optimalPostingTimesBarTooltipLabelSub">Within this chart view</span>
              </span>
            </span>
            <span className="rg-chart__tooltip-value dashboard-tabular-nums">
              {formatPercent(hoveredCell.percent / 100, 1)}
            </span>
          </div>
          {messageVolumeTierTooltipPhrase(hoveredHeatLevel) ? (
            <div className="messageHeatmapTooltipTier optimalPostingTimesTooltipTier">
              {messageVolumeTierTooltipPhrase(hoveredHeatLevel)}
            </div>
          ) : (
            <div className="messageHeatmapTooltipTier messageHeatmapTooltipTier--muted optimalPostingTimesTooltipTier optimalPostingTimesTooltipTier--muted">
              No messages in this slot
            </div>
          )}
        </div>
      </div>,
      document.body,
    )

  const bindHoverWeekly = (day, hourLabel, hourIndex, colIndex, value, percent) => ({
    onMouseEnter: (e) => {
      tooltipAnchorRef.current = e.currentTarget
      setHoveredCell({
        mode: "weekly",
        day,
        hourLabel,
        hourIndex,
        col: colIndex,
        value,
        percent,
      })
    },
    onMouseLeave: () => {
      tooltipAnchorRef.current = null
      setHoveredCell(null)
    },
    onFocus: (e) => {
      tooltipAnchorRef.current = e.currentTarget
      setHoveredCell({
        mode: "weekly",
        day,
        hourLabel,
        hourIndex,
        col: colIndex,
        value,
        percent,
      })
    },
    onBlur: () => {
      tooltipAnchorRef.current = null
      setHoveredCell(null)
    },
  })

  const renderColumns = () => {
    return DISPLAY_DAYS.map(({ label, dataIndex }) => {
      const colIndex = isoDayToSunFirstCol(dataIndex)
      const dayName = wDays[colIndex] ?? label
      return (
        <div key={label} className="optimalPostingTimesBarColumnWrap messageHeatmapBarColumnWrap">
        <div className="optimalPostingTimesBarColumn messageHeatmapBarColumn">
          {HOURS_24.map((h) => {
            const hourLabel = formatAxisHour(h)
            const value = weeklyMatrix24[h]?.[colIndex] ?? 0
            const level = getVolumeBand(value, volumeBands.edges, volumeBands.bandCount)
            const isHighlighted = isHoveredOnly(h, colIndex)
            const bg = heatmapSegmentFill(level, pinnedBand)
            const percent = total ? (value / total) * 100 : 0
            const rankPeak = topPeakKeySet.has(`${h},${colIndex}`)
            return (
              <button
                key={`${label}-${h}`}
                type="button"
                className={[
                  "optimalPostingTimesBarSegment",
                  "messageHeatmapBarSegment",
                  isHighlighted ? "messageHeatmapBarSegment--highlighted" : "",
                  rankPeak ? "messageHeatmapBarSegment--rankPeak" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                style={{ backgroundColor: level === 0 ? "transparent" : bg }}
                aria-label={`${shortenDay(dayName)} ${hourLabel}, ${formatNumber(value)} messages, ${formatPercent(percent / 100, 1)} of message volume`}
                {...bindHoverWeekly(dayName, hourLabel, h, colIndex, value, percent)}
                onClick={(e) => e.stopPropagation()}
              />
            )
          })}
        </div>
      </div>
      )
    })
  }

  const plotInner = (
    <>
      <div className="optimalPostingTimesBarGrid" aria-hidden="true" />
      {peakBandLayoutStyle ? (
        <div className="messageHeatmapPeakHourBand" style={peakBandLayoutStyle} aria-hidden="true" />
      ) : null}
      <div
        className="optimalPostingTimesBarColumns optimalPostingTimesBarColumns--barsViewMotion messageHeatmapBarColumns"
        key={viewMode}
        data-view-shift={chartViewShiftAttr}
      >
        {renderColumns()}
      </div>
    </>
  )

  const emptyMessage = "Not enough message volume to show a weekly heatmap."

  return (
    <ChartPanel
      dataChartId={MESSAGE_HEATMAP_CHART_ID}
      title={panelTitle}
      subtitle={weeklyPeakSubtitle}
      actions={actions}
      footerActions={footerActions}
      loading={loading}
      className="messageHeatmapPanel messageVolumeDailyPanel optimalPostingTimesUnifiedPanel messageHeatmapUnifiedPanel"
    >
      {!loading && !hasEnoughActive ? (
        <div className="optimalPostingTimesEmpty" role="status">
          <p className="optimalPostingTimesEmptyText">{emptyMessage}</p>
        </div>
      ) : (
        <>
          <div
            className="messageHeatmapSignals"
            role="group"
            aria-label="Message volume tier summaries"
            style={{ "--messageHeatmapSignalCols": Math.max(1, bandSummaries.length) }}
          >
            {bandSummaries.map(({ level, slotCount, totalMessages: tierMessages, share }) => {
              const isActive = pinnedBand === level
              const signalMuted = pinnedBand != null && pinnedBand !== level
              const tierName = messageVolumeTierLabel(level)
              return (
                <button
                  key={level}
                  type="button"
                  className={[
                    "messageHeatmapSignal",
                    isActive ? "messageHeatmapSignal--active" : "",
                    signalMuted ? "messageHeatmapSignal--muted" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => togglePinBand(level)}
                  aria-pressed={isActive}
                  aria-label={
                    isActive
                      ? `Clear highlight for ${tierName} volume tier`
                      : `Highlight ${tierName} volume tier on the chart`
                  }
                >
                  <div className="messageHeatmapSignalContent">
                    <span className="messageHeatmapSignalHeading">
                      <span
                        className="campaignPerformanceLegendSwatch messageHeatmapLegendSwatch messageHeatmapSignalSwatch"
                        style={{ backgroundColor: heatmapLegendSwatchFill(level, pinnedBand) }}
                        aria-hidden="true"
                      />
                      <span className="messageHeatmapSignalSlot">{tierName}</span>
                    </span>
                    <span className="messageHeatmapSignalMeta dashboard-tabular-nums">
                      {formatNumber(slotCount)} time slot{slotCount === 1 ? "" : "s"} · {formatNumber(tierMessages)}{" "}
                      messages
                    </span>
                    <span className="messageHeatmapSignalMeta dashboard-tabular-nums">
                      {slotCount > 0 && total > 0 ? `${formatPercent(share, 1)} of total message volume` : "—"}
                    </span>
                    {level === VOLUME_BAND_COUNT && topPeakKeySet.size > 0 ? (
                      <span className="messageHeatmapSignalHint">The three highest-volume slots are outlined on the chart.</span>
                    ) : null}
                  </div>
                </button>
              )
            })}
          </div>
          <div className="optimalPostingTimesMainRow optimalPostingTimesMainRow--chart">
          <div className="optimalPostingTimesBarChartWrap messageHeatmapBarChartWrap">
            <div
              className={`optimalPostingTimesBarChart optimalPostingTimesBarChart--unified${useScrollX ? " optimalPostingTimesBarChart--pannable" : ""}`}
              role="region"
              aria-label="Message volume by day of week and time of day"
              style={chartLayoutVars}
            >
              <div className="optimalPostingTimesBarYAxis messageHeatmapBarYAxis" aria-hidden="true">
                {Y_AXIS_HOURS.map((h) => (
                  <span key={h} className="optimalPostingTimesBarYTick messageHeatmapBarYTick tabular-nums">
                    {formatAxisHour(h)}
                  </span>
                ))}
              </div>
              <div className="optimalPostingTimesBarHScroll">
                <div className="optimalPostingTimesBarHScrollInner">
                  <div className="optimalPostingTimesBarPlot" ref={plotRef}>
                    {plotInner}
                  </div>
                  <div className="optimalPostingTimesBarXAxisTrack">
                    <div className="optimalPostingTimesBarXAxis optimalPostingTimesBarXAxis--weeklyStrip">
                      {DISPLAY_DAYS.map(({ label, weekend }) => (
                        <span
                          key={label}
                          className={`optimalPostingTimesBarDayLabel ${weekend ? "optimalPostingTimesBarDayLabel--weekend" : "optimalPostingTimesBarDayLabel--weekday"}`}
                        >
                          {label}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {barTooltipEl}
            <div className="optimalPostingTimesBarLegendRow messageHeatmapBarLegendRow">
              <div className="campaignPerformanceLegend messageHeatmapBarLegend messageHeatmapBarLegend--tierDefinitions">
                {buildMessageLegendTierRows(volumeBands.edges, volumeBands.bandCount).map(({ level, name, range }) => {
                  const legendActive = pinnedBand === level
                  const legendMuted = pinnedBand != null && pinnedBand !== level
                  return (
                    <div
                      key={level}
                      className={`campaignPerformanceLegendItem messageHeatmapLegendItem messageHeatmapLegendTierItem ${legendActive ? "messageHeatmapLegendItem--active" : ""} ${legendMuted ? "messageHeatmapLegendItem--muted" : ""}`}
                      aria-label={`${name} volume tier: ${range}`}
                    >
                      <span
                        className="campaignPerformanceLegendSwatch messageHeatmapLegendSwatch"
                        style={{ backgroundColor: heatmapLegendSwatchFill(level, pinnedBand) }}
                        aria-hidden="true"
                      />
                      <span className="messageHeatmapLegendTierCopy" aria-hidden="true">
                        <span className="messageHeatmapLegendTierName">{name}</span>
                        <span className="messageHeatmapLegendTierRange">{range}</span>
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
        </>
      )}
    </ChartPanel>
  )
}
